import * as utils from '@/common/utils'
import * as googleMaps from '@/common/googleMaps'
import * as mapUtils from '@/maps/google/mapUtils'
import * as assetMarkers from '@/maps/google/assetMarkers'
import * as trackedVehicleHelpers from '@/store/assets/vehicles/trackedVehicles.helpers'
import * as trackedUnitHelpers from '@/store/units/trackedUnits.helpers'
import * as liveTrackingWidgetHelpers from '@/store/monitoring/liveTrackingWidget.helpers'
import * as widgetControlHelpers from '@/store/monitoring/widgetControl.helpers'
import * as userSettings from '@/store/accounts/usersSettings.helpers'
import * as unitSensors from '@/common/unitSensors'
import * as sse from '@/common/sse'

import ApiService from '@/common/api.service'
import toastr from 'toastr'
import axios from 'axios'
import store from '@/store/index'
import moment from 'moment'

const MAX_SENSOR_ENTRIES = 3600

// We intentionally do not use an arrow function callback here
// so that we can use the "this" context of the function instead
// of the module.
function markerClickHandler () {
  let marker = this

  if (marker.assetType === 'vehicle') {
    widgetControlHelpers.open('LiveTrackingWidget')
    store.commit(`monitoring/monitoringModule/followVehicle`, marker.assetId)
    liveTrackingWidgetHelpers.selectVehicle(marker.assetId)
  } else if (marker.assetType === 'unit') {
    widgetControlHelpers.open('LiveTrackingWidget')
    store.commit(`monitoring/monitoringModule/followUnit`, marker.assetId)
    liveTrackingWidgetHelpers.selectUnit(marker.assetId)
  }

  widgetControlHelpers.close('FindVehiclesWidget')
  widgetControlHelpers.close('RecentVehiclesWidget')
  widgetControlHelpers.close('FindUnitsWidget')
  widgetControlHelpers.close('RecentUnitsWidget')

  // Zoom in if we are zoomed out very far
  if (googleMaps.map.getZoom() <= 8) {
    googleMaps.map.setZoom(17)
  }

  googleMaps.panToMarker(googleMaps.map, marker)
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    // Monitored unit object structure:
    // {
    //  uid: data.uid,          // unit's IMEI or serial no.
    //  unit_assigned_as: 'primary'
    //  unit_id: 1,             // Unit's database id
    //  unit_name: 'blah',      // Unit's name
    //  vehicle_id: 1,          // Vehicle's database id (or null)
    //  vehicle_reg_no: 'blah', // Vehicle's registration no (or null)
    //  vehicle_fleet_no: 'fleet no',
    //  vehicle_group_name: 'group name',
    //  asset: 'vehicle',       // Asset can be 'vehicle', 'unit' or 'driver'
    //  driver_id: 1,           // Driver's database id (or null)
    //  sseStatus: 'none',
    //  token: data.token,
    //  telemetry: data.telemetry
    // }

    monitoredAssets: [],
    followingUnitId: null,
    followingVehicleId: null,
    followingAsset: null,
    mapCenter: null,
    mapZoomLevel: null,
    recordingSensorName: null, // Name of the sensor being recorded
    recordingSensorValueIndex: null, // Value Index for sensors with array based values
    prevRecordingSensorName: null,
    sensorHistory: [],
    sensorHistoryLoaded: false,
    sensorHasHistoryData: false,
    onSensorHistoryData: null
  }
}

export const state = {
  data: Object.assign({}, defaults())
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the array of objects of the unit's being monitored.
   *
   * @return array
   * @return array
   */
  getMonitoredAssets (state) {
    return state.data.monitoredAssets
  },

  /**
   * Returns the id of the unit being followed.
   *
   * @param state
   * @returns null|int
   */
  getFollowingUnitId (state) {
    return state.data.followingUnitId
  },

  /**
   * Returns the id of the vehicle being followed.
   *
   * @param state
   * @returns null|int
   */
  getFollowingVehicleId (state) {
    return state.data.followingVehicleId
  },

  /**
   * Returns the data of the asset being followed.
   *
   * @param state
   * @returns null|Object
   */
  getFollowingAsset (state) {
    return state.data.followingAsset
  },

  /**
   * Returns the map's stored center.
   *
   * @return string
   */
  getMapCenter (state) {
    return state.data.mapCenter
  },

  /**
   * Returns the map's stored zoom level.
   *
   * @return string
   */
  getMapZoomLevel (state) {
    return state.data.mapZoomLevel
  },

  /**
   * Returns the name of the sensor which is currently being recorded, or
   * null if none is being recorded.
   *
   * @param state
   *
   * @returns String|null
   */
  getRecordingSensorName (state) {
    return state.data.recordingSensorName
  },

  /**
   * Returns the index of the sensor's value which is currently being recorded for
   * sensors with array data.
   *
   * @param state
   *
   * @returns String|null
   */
  getRecordingSensorValueIndex (state) {
    return state.data.recordingSensorValueIndex
  },

  /**
   * Returns the name of the previous sensor that was being recorded
   *
   * @param state
   * @returns String|null
   */
  getPrevRecordingSensorName (state) {
    return state.data.prevRecordingSensorName
  },

  /**
   * Returns a sensor's history.
   *
   * @param state
   *
   * @returns []
   */
  getSensorHistory (state) {
    return state.data.sensorHistory
  },

  /**
   * Return sensor history data state
   *
   * @param state
   * @returns boolean
   */
  sensorHasHistoryData (state) {
    return state.data.sensorHasHistoryData
  },

  /**
   * Returns true or false depending on whether the sensor history
   * has been loaded from the history/telemetry API.
   *
   * @param state
   *
   * @returns Boolean
   */
  getSensorHistoryLoaded (state) {
    return state.data.sensorHistoryLoaded
  },

  /**
   * Returns the function that is bound as an event handler for when
   * new data is received.
   *
   * @param state
   * @returns null|Function
   */
  getOnSensorHistoryData (state) {
    return state.data.onSensorHistoryData
  }
}

export const actions = {
  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`vehicles`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Starts the processes of subscribing to an SSE server to receive data
   * for the unit allocated to the specified vehicle.
   *
   * @param context Object
   * @param vehicleId int
   * @param assignedAs string
   * @param follow boolean
   *
   * @return Promise
   */
  sseSubscribeVehicle (context, { vehicleId, assignedAs, follow }) {
    // First we need to get a JWT token from the web server
    return ApiService
      .get(`monitoring/vehicle/${vehicleId}/${assignedAs}`)
      .then((response) => {
        // Now we have a JWT token and other relevant data.
        const data = response.data.data
        console.log('SSE: Subscription JWT token received.', data)

        if (follow) {
          store.commit(`monitoring/monitoringModule/setFollowingVehicleId`, vehicleId)
        }

        let monitoredAssets = store.getters['monitoring/monitoringModule/getMonitoredAssets']
        let vehicle = monitoredAssets.find((element) => {
          return element.uid === data.uid
        })

        if (!vehicle) {
          // Add the vehicle to the list of monitored vehicles if it
          // isn't added already.

          vehicle = {
            uid: data.uid,
            unit_id: data.unit_id,
            unit_name: data.unit_name,
            unit_assigned_as: assignedAs,
            vehicle_id: data.vehicle_id,
            vehicle_reg_no: data.vehicle_reg_no,
            vehicle_fleet_no: data.vehicle_fleet_no,
            vehicle_icon_name: data.vehicle_icon_name,
            vehicle_group_name: data.vehicle_group_name,
            driver_id: data.driver_id,
            asset: 'vehicle',
            sseStatus: 'none',
            an_inputs: data.an_inputs,
            token: data.token,
            telemetry: data.telemetry,
            marker: null
          }
          store.commit(`monitoring/monitoringModule/addMonitoredAsset`, vehicle)
        }

        assetMarkers.addMarkerClickHandler('monitoring-clicks', markerClickHandler)

        // If we have a stream id (meaning the event source is connected and ready)
        // we can send a subscription request to the SSE server now, otherwise
        // the subscription will automatically be done once the stream id has been
        // received from the SSE server
        if (sse.getStreamId() !== null) {
          store.commit(`monitoring/monitoringModule/sseSubscribe`, vehicle)
        }

        trackedVehicleHelpers.setAnalogInputCfg(vehicle.vehicle_id, vehicle.an_inputs)
        if (vehicle.telemetry !== null) {
          store.commit(`monitoring/monitoringModule/setMarker`, { asset: vehicle })
          trackedVehicleHelpers.setTelemetry(vehicle.vehicle_id, vehicle.telemetry)
        } else {
          toastr.warning(`There are currently no telemetry data for one or more vehicles you have selected`, 'Warning')
        }

        return response
      })
  },

  /**
   * Starts the processes of subscribing to an SSE server to receive data
   * for the specified unit.
   *
   * @param context Object
   * @param unitId int
   * @param follow boolean
   *
   * @return Promise
   */
  sseSubscribeUnit (context, { unitId, follow }) {
    // First we need to get a JWT token from the web server
    return ApiService
      .get(`monitoring/unit/${unitId}`)
      .then((response) => {
        // Now we have a JWT token and other relevant data.
        const data = response.data.data
        console.log('SSE: Subscription JWT token received.', data)

        if (follow) {
          store.commit(`monitoring/monitoringModule/setFollowingUnitId`, unitId)
        }

        // Add the unit to the list of monitored units if it
        // isn't added already.
        let monitoredAssets = store.getters['monitoring/monitoringModule/getMonitoredAssets']
        let unit = monitoredAssets.find((element) => {
          return element.uid === data.uid
        })

        if (!unit) {
          unit = {
            uid: data.uid,
            unit_id: data.unit_id,
            unit_name: data.unit_name,
            unit_assigned_as: null,
            vehicle_id: data.vehicle_id,
            vehicle_reg_no: data.vehicle_reg_no,
            vehicle_fleet_no: null,
            vehicle_icon_name: null,
            vehicle_group_name: null,
            driver_id: data.driver_id,
            asset: 'unit',
            sseStatus: 'none',
            an_inputs: data.an_inputs,
            token: data.token,
            telemetry: data.telemetry,
            marker: null
          }
          store.commit(`monitoring/monitoringModule/addMonitoredAsset`, unit)
        }

        assetMarkers.addMarkerClickHandler('monitoring-clicks', markerClickHandler)

        // If we have a stream id (meaning the event source is connected and ready)
        // we can send a subscription request to the SSE server now, otherwise
        // the subscription will automatically be done once the stream id has been
        // received from the SSE server
        if (sse.getStreamId() !== null) {
          store.commit(`monitoring/monitoringModule/sseSubscribe`, unit)
        }

        trackedUnitHelpers.setAnalogInputCfg(unit.unit_id, unit.an_inputs)
        if (unit.telemetry !== null) {
          store.commit(`monitoring/monitoringModule/setMarker`, { asset: unit })
          trackedUnitHelpers.setTelemetry(unit.unit_id, unit.telemetry)
        } else {
          toastr.warning(`There are currently no telemetry data for one or more units you have selected`, 'Warning')
        }

        return response
      })
  },

  /**
   * Start's the re-subscription process. Calling this will
   * automatically re-subscribe all previously selected
   * vehicles/units to the SSE server.
   *
   * @param context Object
   *
   * @return Promise
   */
  sseResubscribe (context) {
    // Clear the subscription status of all monitored units
    // so that they will be re-subscribed
    let monitoredAssets = context.getters['getMonitoredAssets']
    monitoredAssets.forEach((asset) => {
      asset.sseStatus = 'none'
    })

    // Start the SSE subscription process...
    if (monitoredAssets.length > 0) {
      let asset = monitoredAssets[0]
      if (asset.asset === 'unit') {
        return store.dispatch(
          `monitoring/monitoringModule/sseSubscribeUnit`,
          { unitId: asset.unit_id, follow: false }
        )
      } else if (asset.asset === 'vehicle') {
        return store.dispatch(
          `monitoring/monitoringModule/sseSubscribeVehicle`,
          { vehicleId: asset.vehicle_id, assignedAs: asset.unit_assigned_as, follow: false }
        )
      }
    }
  },

  /**
   * Submits a POST to unsubscribe a specific asset
   * @param context
   * @param asset
   */
  ssePostUnsubAsset (context, asset) {
    const sseUrl = sse.getSseUrl()
    const sseStreamId = sse.getStreamId()
    console.log(`SSE: Requesting unsubscription for unit: ${asset.uid}...`)
    const url = `${sseUrl}telemetry/unsubscribe/${sseStreamId}/${asset.token}`
    // Send an unsubscribe request to the SSE server
    return axios.post(url, {}, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then(() => {
      console.log(`SSE: Unsubscription for unit: ${asset.uid} successful`)
    })
  },

  /**
   * Loads the sensor history for the sensor that is currently being recorded.
   *
   * @param context
   * @param telemetry
   *
   * @return Promise
   */
  loadSensorHistory (context, telemetry) {
    store.commit(`monitoring/monitoringModule/setSensorHistoryLoaded`, false)

    let resource = null
    let sensor = null
    let monitoredAssets = context.getters['getMonitoredAssets']
    if (monitoredAssets.length > 0) {
      let asset = monitoredAssets[0]
      let date, from, to
      if (asset.telemetry !== null) {
        let localTime = moment(asset.telemetry.gps.local_time)
        date = localTime.format('YYYY-MM-DD')
        to = localTime.format('HH:mm:ss')
        from = localTime.subtract(15, 'minutes').format('HH:mm:ss')
        sensor = context.getters['getRecordingSensorName']
        let sensorField
        if (sensor.indexOf('.') === -1) {
          // If no dot notation was used, we assume the sensor being recorded
          // is in the sensors section of telemetry data
          sensorField = `sensors.${sensor}`
        } else {
          sensorField = sensor
        }

        const fields = `_fields=gps.local_time,${sensorField}`
        if (asset.asset === 'unit') {
          resource = `history/telemetry/unit/${asset.unit_id}/date/${date}/from/${from}/to/${to}?${fields}`
        } else if (asset.asset === 'vehicle') {
          resource = `history/telemetry/vehicle/${asset.vehicle_id}/${asset.unit_assigned_as}/date/${date}/from/${from}/to/${to}?${fields}`
        }
      }
    }

    if (resource !== null) {
      return ApiService
        .get(resource)
        .then((response) => {
          if (response.data.data.length === 0) {
            if (telemetry !== null) {
              if (unitSensors.sensorAvailable(sensor)) {
                const withTimestamp = unitSensors.sensors[sensor].get(telemetry)
                store.commit(`monitoring/monitoringModule/addToSensorHistory`, withTimestamp)
              }
            }
          } else {
            store.commit(`monitoring/monitoringModule/setSensorHistory`, response.data.data)
          }
          store.commit(`monitoring/monitoringModule/setSensorHistoryLoaded`, true)
          return response
        })
    } else {
      return Promise.resolve(null)
    }
  },

  enableVehicleSafeZone (context, { id, radius, mode }) {
    return ApiService
      .post(`vehicles/${id}/primary/safe-zone`, { radius: radius, mode: mode })
      .then((response) => {
        return response
      })
  },

  disableVehicleSafeZone (context, id) {
    return ApiService
      .delete(`vehicles/${id}/primary/safe-zone`)
      .then((response) => {
        return response
      })
  },

  enableUnitSafeZone (context, { id, radius, mode }) {
    return ApiService
      .post(`units/${id}/safe-zone`, { radius: radius, mode: mode })
      .then((response) => {
        return response
      })
  },

  disableUnitSafeZone (context, id) {
    return ApiService
      .delete(`units/${id}/safe-zone`)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = {}
  },

  /**
   * Store the map's center.
   *
   * @param state
   * @param center google.maps.LatLng
   */
  storeMapCenter (state, center) {
    state.data.mapCenter = center
  },

  /**
   * Store the map's zoom level.
   *
   * @param state
   * @param zoomLevel int
   */
  storeMapZoomLevel (state, zoomLevel) {
    state.data.mapZoomLevel = zoomLevel
  },

  /**
   * Sets the vehicle to be followed.
   *
   * @param state
   * @param vehicleId int
   */
  setFollowingVehicleId (state, vehicleId) {
    state.data.followingUnitId = null
    state.data.followingVehicleId = vehicleId
    state.data.followingAsset = { type: 'vehicle', id: vehicleId }
  },

  /**
   * Sets the unit to be followed.
   *
   * @param state
   * @param unitId int
   */
  setFollowingUnitId (state, unitId) {
    state.data.followingVehicleId = null
    state.data.followingUnitId = unitId
    state.data.followingAsset = { type: 'unit', id: unitId }
  },

  /**
   * Starts following the specific vehicle.
   *
   * @param state Object
   * @param vehicleId int
   */
  followVehicle (state, vehicleId) {
    let vehicle = state.data.monitoredAssets.find(asset => {
      if (asset.vehicle_id === vehicleId) {
        return asset
      }
    })

    if (vehicle) {
      state.data.followingUnitId = null
      state.data.followingVehicleId = vehicleId
      state.data.followingAsset = { type: 'vehicle', id: vehicleId }
      assetMarkers.setTargetMarker(googleMaps.map, vehicle.telemetry)
      googleMaps.panToMarker(googleMaps.map, assetMarkers.getAssetMarker(vehicleId, 'vehicle'))
    }
  },

  /**
   * Starts following the specific unit.
   *
   * @param state Object
   * @param unitId int
   */
  followUnit (state, unitId) {
    let unit = state.data.monitoredAssets.find(asset => {
      if (asset.unit_id === unitId) {
        return asset
      }
    })

    if (unit) {
      state.data.followingVehicleId = null
      state.data.followingUnitId = unitId
      state.data.followingAsset = { type: 'unit', id: unitId }
      assetMarkers.setTargetMarker(googleMaps.map, unit.telemetry)
      googleMaps.panToMarker(googleMaps.map, assetMarkers.getAssetMarker(unitId, 'unit'))
    }
  },

  /**
   * Unfollows all units/vehicles.
   *
   * @param state
   */
  unfollowAll (state) {
    state.data.followingUnitId = null
    state.data.followingVehicleId = null
    state.data.followingAsset = null
    assetMarkers.clearTargetMarker()
  },

  /**
   * Add an asset to the list of monitored assets.
   *
   * @param state
   * @param asset object
   */
  addMonitoredAsset (state, asset) {
    state.data.monitoredAssets.push(asset)
  },

  /**
   * Initializes an SSE connection if one isn't setup already.
   *
   * @param state Object
   */
  sseInitialize (state) {
    sse.sseInit('telemetry')
  },

  /**
   * This needs to be called when an SSE error occurred
   *
   * @param state
   */
  sseHandleError (state) {
    // Clear the subscription status of all monitored units
    // so that they will be re-subscribed if we do end up
    // connecting again.
    state.data.monitoredAssets.forEach((asset) => {
      asset.sseStatus = 'none'
    })
  },

  /**
   * Unsubscribes the unit of the specified vehicle from the SSE server
   *
   * @param state Object
   * @param vehicleId int
   * @param assignedAs string
   */
  sseUnsubscribeVehicle (state, { vehicleId, assignedAs }) {
    const vehicle = state.data.monitoredAssets.find((element) => {
      return element.vehicle_id === vehicleId
    })

    if (vehicle) {
      console.log(`SSE: Requesting unsubscription for unit: ${vehicle.uid}...`)
      const sseUrl = sse.getSseUrl()
      const sseStreamId = sse.getStreamId()
      const url = `${sseUrl}telemetry/unsubscribe/${sseStreamId}/${vehicle.token}`
      vehicle.sseStatus = 'unsubscribing'

      // Send an unsubscribe request to the SSE server
      return axios.post(url, {}, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }).then(function (response) {
        const uid = response.data.uid
        const index = state.data.monitoredAssets.findIndex((element) => {
          return element.uid === uid
        })

        if (index > -1) {
          // Clear the marker
          assetMarkers.removeAssetMarker(vehicleId, 'vehicle')
          state.data.monitoredAssets.splice(index, 1)
          store.commit(`monitoring/monitoringModule/checkActiveTarget`)
        }
        console.log(`SSE: Unsubscription for unit: ${uid} successful`)
      })
    }
  },

  /**
   * Unsubscribes the specified unit from the SSE server
   *
   * @param state Object
   * @param unitId int
   */
  sseUnsubscribeUnit (state, { unitId }) {
    const unit = state.data.monitoredAssets.find((element) => {
      return element.unit_id === unitId
    })

    if (unit) {
      console.log(`SSE: Requesting unsubscription for unit: ${unit.uid}...`)
      const sseUrl = sse.getSseUrl()
      const sseStreamId = sse.getStreamId()
      const url = `${sseUrl}telemetry/unsubscribe/${sseStreamId}/${unit.token}`
      unit.sseStatus = 'unsubscribing'

      // Send an unsubscribe request to the SSE server
      return axios.post(url, {}, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      }).then(function (response) {
        const uid = response.data.uid
        const index = state.data.monitoredAssets.findIndex((element) => {
          return element.uid === uid
        })

        if (index > -1) {
          // Clear the marker
          assetMarkers.removeAssetMarker(unitId, 'unit')
          state.data.monitoredAssets.splice(index, 1)
          store.commit(`monitoring/monitoringModule/checkActiveTarget`)
        }
        console.log(`SSE: Unsubscription for unit: ${uid} successful`)
      })
    }
  },

  /**
   * Unsubscribes all units and vehicles from the SSE server depending
   * on the assetType argument
   *
   * @param state
   * @param assetType Can be 'all', 'units', 'vehicles'
   */
  sseUnsubscribeAll (state, assetType = 'all') {
    // We make a copy of the currently monitored assets so we can
    // clear the monitoredAssets array immediately. This means the
    // calling function can start adding new assets immediately.
    const prevMonitoredAssets = state.data.monitoredAssets

    if (assetType === 'all') {
      state.data.monitoredAssets = []
    } else if (assetType === 'vehicles') {
      state.data.monitoredAssets = state.data.monitoredAssets.filter(asset => asset.asset !== 'vehicle')
    } else if (assetType === 'units') {
      state.data.monitoredAssets = state.data.monitoredAssets.filter(asset => asset.asset !== 'unit')
    }

    prevMonitoredAssets.forEach((asset) => {
      if (asset.asset === 'unit' && (assetType === 'all' || assetType === 'units')) {
        assetMarkers.removeAssetMarker(asset.unit_id, 'unit')
        return store.dispatch(`monitoring/monitoringModule/ssePostUnsubAsset`, asset)
      } else if (asset.asset === 'vehicle' && (assetType === 'all' || assetType === 'vehicles')) {
        assetMarkers.removeAssetMarker(asset.vehicle_id, 'vehicle')
        return store.dispatch(`monitoring/monitoringModule/ssePostUnsubAsset`, asset)
      }
    })

    store.commit(`monitoring/monitoringModule/checkActiveTarget`)
  },

  /**
   * Unsubscribes all units and vehicles from the SSE server but keep
   * them in the asset list for re-subscription later on.
   *
   * @param state
   */
  sseUnsubAllButKeep (state) {
    state.data.monitoredAssets.forEach((asset) => {
      asset.sseStatus = 'none'
      // store.dispatch(`monitoring/monitoringModule/ssePostUnsubAsset`, asset)
    })

    // noinspection JSIgnoredPromiseFromCall
    sse.unsubscribe('telemetry')
  },

  /**
   * Requests a subscription for the given unit on the SSE server. Once
   * done, it will follow up with any other outstanding units that still
   * needs to be subscribed.
   *
   * @param state Object
   * @param unit  Object
   */
  sseSubscribe (state, unit) {
    console.log(`SSE: Requesting subscription for unit: ${unit.uid}...`)
    const sseUrl = sse.getSseUrl()
    const sseStreamId = sse.getStreamId()
    const url = `${sseUrl}telemetry/subscribe/${sseStreamId}/${unit.token}`
    unit.sseStatus = 'subscribing'

    // Submit the request to the SSE server.
    axios.post(url, {}, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    }).then(function (response) {
      const uid = response.data.uid
      console.log(`SSE: Subscription for unit: ${uid} successful`)

      // Search for the unit that was successfully
      // subscribed.
      let subscribedAsset = state.data.monitoredAssets.find((element) => {
        return element.uid === uid
      })

      if (subscribedAsset) {
        // Update the unit's subscription status
        subscribedAsset.sseStatus = 'subscribed'
      }

      // Search for next that needs subscription
      let nextAsset = state.data.monitoredAssets.find((element) => {
        return element.sseStatus === 'none'
      })

      if (nextAsset) {
        // Call ourselves to perform the next subscription
        console.log('SSE: Subscribing next asset', nextAsset)
        if (nextAsset.asset === 'unit') {
          return store.dispatch(
            `monitoring/monitoringModule/sseSubscribeUnit`,
            { unitId: nextAsset.unit_id, follow: false }
          )
        } else if (nextAsset.asset === 'vehicle') {
          return store.dispatch(
            `monitoring/monitoringModule/sseSubscribeVehicle`,
            { vehicleId: nextAsset.vehicle_id, assignedAs: nextAsset.unit_assigned_as, follow: false }
          )
        }
      }
    })
  },

  /**
   * If an event source is open then close it.
   *
   * @param state  Object
   */
  sseClose (state) {
    sse.sseClose()
  },

  /**
   * Called when the SSE stream is ready to be used. If there are
   * units that are not subscribed yet this will kick off the process of
   * subscribing them.
   *
   * @param state Object
   */
  handleSseReady (state) {
    // We can start subscribing units to the SSE server
    // Find the first unit that is not subscribed:
    let asset = state.data.monitoredAssets.find((element) => {
      return element.sseStatus === 'none'
    })

    if (asset) {
      store.commit(`monitoring/monitoringModule/sseSubscribe`, asset)
    }
  },

  /**
   * Handles a gps event from the event source connection.
   *
   * @param state Object
   * @param data Object
   */
  handleGpsEvent (state, data) {
    let uid

    if (data.data.message_type !== 'gps') {
      // message_type is either "gps" or "trip", we only deal with
      // "gps" message types at the moment. Note that this is not
      // the same as the data.data.type property which is passed along
      // from the SSE MQBS service.
      return
    }

    if (data.data.device.identifier === 'imei') {
      uid = data.data.device.imei
    } else {
      uid = data.data.device.serial_no
    }

    let asset = state.data.monitoredAssets.find((element) => {
      return element.uid === uid
    })

    if (asset) {
      // Determine if the GPS timestamp is newer than the previous GPS timestamp,
      // This is so that the unit/vehicle does not jump backwards to an older
      // position if the unit transmitted data in the wrong order.
      if (asset.telemetry === null || moment(data.data.gps.timestamp).isSameOrAfter(asset.telemetry.gps.timestamp)) {
        asset.telemetry = data.data
        store.commit(`monitoring/monitoringModule/setMarker`, { asset })

        if (asset.asset === 'unit') {
          trackedUnitHelpers.setTelemetry(asset.unit_id, asset.telemetry)
        } else if (asset.asset === 'vehicle') {
          trackedVehicleHelpers.setTelemetry(asset.vehicle_id, asset.telemetry)
        }

        if (state.data.recordingSensorName !== null && state.data.sensorHistoryLoaded) {
          const withTimestamp = unitSensors.withTimestamp(state.data.recordingSensorName, asset.telemetry)
          if (withTimestamp !== null) {
            store.commit(`monitoring/monitoringModule/addToSensorHistory`, withTimestamp)
          }
        }

        console.log('SSE: Updated unit GPS telemetry data.')
        // eventBus.$emit('unit_gps', unit)
      } else {
        console.log('SSE: SKIPPED Updating unit GPS telemetry data.')
      }
    }
  },

  /**
   * Adds or updates a vehicle or unit's marker and label. The marker
   * requires a type which is usually retrieved from the asset data, this
   * is typically either 'unit' or 'vehicle'. This type can be overwritten
   * using the overrideType argument.
   *
   * @param state
   * @param asset
   * @param overrideType null|string
   */
  setMarker (state, { asset, overrideType = null }) {
    let stateIcon
    if (asset.telemetry.gps.speed > 0) {
      stateIcon = assetMarkers.labelStates.moving
    } else {
      stateIcon = assetMarkers.labelStates.stopped
    }

    // Determine what to display on the marker's label
    let markerContent
    let assetId
    let followingAssetId
    let assetIcon

    if (asset.asset === 'vehicle') {
      let labelValues = []
      let labelCount = 0

      if (userSettings.get('lt_lbl_veh_fleet_no', false) && asset.vehicle_fleet_no !== null) {
        labelValues.push('<div>Fleet: ' + asset.vehicle_fleet_no + '</div>')
        labelCount += 1
      }

      if (userSettings.get('lt_lbl_veh_group', false) && asset.vehicle_group_name !== null) {
        labelValues.push('<div>Group: ' + asset.vehicle_group_name + '</div>')
        labelCount += 1
      }

      if (userSettings.get('lt_lbl_unit_id', false) && asset.uid !== null) {
        labelValues.push('<div>Unit: ' + asset.uid + '</div>')
        labelCount += 1
      }

      if (userSettings.get('lt_lbl_unit_name', false) && asset.unit_name !== null) {
        labelValues.push('<div>Name: ' + asset.unit_name + '</div>')
        labelCount += 1
      }

      if (labelCount > 0) {
        labelValues.unshift('<div class="border-bottom mb-1">' + stateIcon + ' ' + asset.vehicle_reg_no + '</div>')
      } else {
        labelValues.unshift('<div>' + stateIcon + ' ' + asset.vehicle_reg_no + '</div>')
      }

      markerContent = labelValues.join('')

      assetId = asset.vehicle_id
      followingAssetId = state.data.followingVehicleId
      assetIcon = asset.vehicle_icon_name === null ? assetMarkers.defaultVehicleIcon : asset.vehicle_icon_name
    } else if (asset.asset === 'unit') {
      markerContent = stateIcon + ' ' + asset.uid

      assetId = asset.unit_id
      followingAssetId = state.data.followingUnitId
      assetIcon = 'blue-circle-arrow.svg'
    }

    if (overrideType !== null) {
      assetMarkers.setAssetMarker(
        googleMaps.map, assetId, overrideType, assetIcon, asset.telemetry, markerContent)
    } else {
      assetMarkers.setAssetMarker(
        googleMaps.map, assetId, asset.asset, assetIcon, asset.telemetry, markerContent)
    }

    if (assetId === followingAssetId) {
      assetMarkers.setTargetMarker(googleMaps.map, asset.telemetry)
      let mapContainer = document.getElementById('monitoring-map')
      let marker = assetMarkers.getAssetMarker(assetId, asset.asset)
      if (mapUtils.isNearMapEdge(mapContainer, marker.getPosition())) {
        googleMaps.panToMarker(googleMaps.map, marker)
      }
    }
  },

  /**
   * Removes a vehicle or unit's marker and label. The marker requires a type
   * which is usually retrieved from the asset data, this is typically either
   * 'unit' or 'vehicle'. This type can be overwritten using the overrideType
   * argument.
   *
   * @param state
   * @param asset
   * @param overrideType null|string
   */
  removeMarker (state, { asset, overrideType = null }) {
    let assetId
    if (asset.asset === 'vehicle') {
      assetId = asset.vehicle_id
    } else if (asset.asset === 'unit') {
      assetId = asset.unit_id
    }

    if (overrideType !== null) {
      assetMarkers.removeAssetMarker(assetId, overrideType)
    } else {
      assetMarkers.removeAssetMarker(assetId, asset.asset)
    }
  },

  /**
   * Clears all markers for all units being monitored.
   *
   * @param state
   */
  clearAllMarkers (state) {
    assetMarkers.clearTargetMarker()
    assetMarkers.clearAssetMarkers()
  },

  /**
   * Redraw's all markers.
   *
   * @param state
   */
  redrawAllMarkers (state) {
    state.data.monitoredAssets.forEach(asset => {
      store.commit(`monitoring/monitoringModule/setMarker`, { asset })
    })
  },

  /**
   * Set the name of the sensor for which we will be recording history. Optionally
   * a valueIndex can be provided to tell the module which value to extract from
   * array based sensors.
   *
   * @param state
   * @param sensor
   * @param valueIndex
   * @param telemetry
   */
  recordSensorHistory (state, { sensor, valueIndex, telemetry }) {
    state.data.prevRecordingSensorName = state.data.recordingSensorName

    if (sensor !== null && (state.data.recordingSensorName !== sensor || valueIndex !== state.data.recordingSensorValueIndex)) {
      state.data.recordingSensorName = sensor
      state.data.recordingSensorValueIndex = valueIndex
      store.commit(`monitoring/monitoringModule/clearSensorHistory`)
      store.dispatch(`monitoring/monitoringModule/loadSensorHistory`, telemetry)
    } else if (sensor === null) {
      // Clear the sensor history as no sensor is selected to be recorded.
      // state.data.sensorHistory = []
      state.data.recordingSensorName = null
      state.data.recordingSensorValueIndex = null
    }
    // else, the sensor is the same as previous, we don't have to do anything
  },

  /**
   * Set the name of the previous sensor that was being recorded.
   *
   * @param state
   * @param name
   */
  setPrevSensorName (state, name) {
    state.data.prevRecordingSensorName = name
  },

  /**
   * Add to the sensor's history.
   *
   * @param state
   *
   * @param data Object In the format { local_time: '13:55:06', value: 17.5 }
   */
  addToSensorHistory (state, data) {
    let sensorHistory = [[], []]

    sensorHistory[0].push(data.local_time)
    if (state.data.recordingSensorValueIndex !== null &&
      data.value.constructor === Array &&
      data.value.length > state.data.recordingSensorValueIndex) {
      sensorHistory[1].push(data.value[state.data.recordingSensorValueIndex])
    } else {
      sensorHistory[1].push(data.value)
    }

    if (state.data.onSensorHistoryData !== null) {
      state.data.onSensorHistoryData(state.data.recordingSensorName, sensorHistory, 'append')
    }

    if (sensorHistory[1].length > 0 && state.data.sensorHasHistoryData === false) {
      state.data.sensorHasHistoryData = true
    }
  },

  /**
   * Set the sensor's history.
   *
   * @param state
   *
   * @param history []
   */
  setSensorHistory (state, history) {
    let sensorHistory = [[], []]
    let entriesAdded = 0
    if (unitSensors.sensorAvailable(state.data.recordingSensorName)) {
      const extractFunction = unitSensors.sensors[state.data.recordingSensorName].get
      history.forEach((telemetry) => {
        if (sensorHistory.length > MAX_SENSOR_ENTRIES) {
          // Remove the first data entries from the x axis and the y axis. Note
          // that the first elements contains the axis labels and not data.
          sensorHistory[0].splice(0, 1)
          sensorHistory[1].splice(0, 1)
        }

        const data = extractFunction(telemetry)
        if (data !== null) {
          entriesAdded += 1
          sensorHistory[0].push(data.local_time)
          if (state.data.recordingSensorValueIndex !== null &&
            data.value.constructor === Array &&
            data.value.length > state.data.recordingSensorValueIndex) {
            sensorHistory[1].push(data.value[state.data.recordingSensorValueIndex])
          } else {
            sensorHistory[1].push(data.value)
          }
        }
      })
    }

    if (sensorHistory[0].length > 0 && sensorHistory[1].length > 0) {
      state.data.sensorHasHistoryData = true
    }

    if (state.data.onSensorHistoryData !== null && entriesAdded > 0) {
      state.data.onSensorHistoryData(state.data.recordingSensorName, sensorHistory, 'set')
    }
  },

  /**
   * Sets whether the sensor history data have been loaded from the history/telemetry
   * API.
   *
   * @param state
   * @param loaded Boolean
   */
  setSensorHistoryLoaded (state, loaded) {
    state.data.sensorHistoryLoaded = loaded
  },

  /**
   * Clear the sensor's history.
   *
   * @param state
   */
  clearSensorHistory (state) {
    if (state.data.onSensorHistoryData !== null) {
      state.data.sensorHasHistoryData = false
      state.data.onSensorHistoryData(state.data.recordingSensorName, [], 'set')
    }
  },

  /**
   * Sets an event handler for when new sensor history data arrives.
   * The function must expect 3 arguments:
   * sensor, data, mode
   *
   * sensor = A string containing the name of the current sensor
   * data   = An array containing the data that must be added to a chart
   * mode   = 'set' or 'append' ('set' = Set the data, 'append' = append to existing data)
   *
   * @param state
   * @param onSensorHistoryData Function
   */
  setOnSensorHistoryData (state, onSensorHistoryData) {
    state.data.onSensorHistoryData = onSensorHistoryData
  },

  /**
   * Determines whether the active crosshair icon should still be displayed, if
   * not then remove it.
   *
   * @param state
   */
  checkActiveTarget (state) {
    let followingAsset = null
    if (state.data.followingVehicleId !== null) {
      followingAsset = state.data.monitoredAssets.find((asset) => {
        return asset.type === 'vehicle' && asset.vehicle_id === state.data.followingVehicleId
      })
    } else if (state.data.followingUnitId !== null) {
      followingAsset = state.data.monitoredAssets.find((asset) => {
        return asset.type === 'unit' && asset.unit_id === state.data.followingUnitId
      })
    }

    if (followingAsset === undefined) {
      assetMarkers.clearTargetMarker()
    }
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.data[name].push(value)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/monitoring/monitoringModule.js